
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { TelemetryPoint, InspectionMode, Finding, Severity } from '../types';
import { TrajectoryMapM } from './TrajectoryMapM';
import { generateCylindricalRoute, generateHorizontalVesselRoute, generateCylinderPointCloud } from '../services/planningService';
import { parseLasFile, parseTextPointCloud, calculateAssetGeometry, parseTrajectoryCsv, detectMissionStart, filterPointsToAssetBoundary, calculateSurfaceFootprint } from '../services/telemetryService';

interface MissionPlannerProps {
    onCancel: () => void;
    onTransfer: (
        video: File | null,
        trajectory: TelemetryPoint[],
        points: TelemetryPoint[],
        overlap?: number,
        lasModelName?: string | null,
        geometry?: any
    ) => void;
    initialCloudOffset?: { x: number, y: number };
    onAlignChange?: (offset: { x: number, y: number }) => void;
}

const severityColors: Record<Severity, string> = {
    [Severity.NONE]: '#10b981',
    [Severity.LOW]: '#0ea5e9',
    [Severity.MEDIUM]: '#eab308',
    [Severity.HIGH]: '#f97316',
    [Severity.CRITICAL]: '#ef4444',
};

export const MissionPlanner: React.FC<MissionPlannerProps> = ({ onCancel, onTransfer, initialCloudOffset, onAlignChange }) => {
    const [phase, setPhase] = useState<'IDLE' | 'EXPLORING' | 'MAPPED' | 'PLANNING'>('IDLE');
    const [setupStep, setSetupStep] = useState<'PLACEMENT' | 'READY'>('PLACEMENT');
    const [homePointSet, setHomePointSet] = useState(false);
    const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    const [routeAccepted, setRouteAccepted] = useState(false);
    const [checklistStep4, setChecklistStep4] = useState(false);
    const [aiMode, setAiMode] = useState<'VIDEO' | 'LIVE' | null>('VIDEO');
    const [checklistStep6, setChecklistStep6] = useState(false);
    const isAutonomousRunRef = useRef(false);

    const [currentTime, setCurrentTime] = useState(0);
    const [isSimulatingRoute, setIsSimulatingRoute] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [simulationComplete, setSimulationComplete] = useState(false);
    const [simulationSpeed, setSimulationSpeed] = useState(1.0);
    const simulationSpeedRef = useRef(1.0);

    const [aiFindings, setAiFindings] = useState<Finding[]>([]);
    const animationFrameRef = useRef<number | null>(null);
    const [showCapOnly, setShowCapOnly] = useState(false);
    const [hideCameras, setHideCameras] = useState(false);
    const [hidePath, setHidePath] = useState(false);

    // 100% to 0% is 9 minutes (540s). Swap at 10% (Used: 486s)
    const BATTERY_MAX_SEC = 540;
    const RTH_TRIGGER_SEC = 486;

    const [batterySecondsUsed, setBatterySecondsUsed] = useState(0);
    const [batteryCycleCount, setBatteryCycleCount] = useState(1);

    const [swapPhase, setSwapPhase] = useState<'NONE' | 'RTH_FLIGHT' | 'SWAPPING' | 'RETURN_FLIGHT'>('NONE');
    const [resumeTimestamp, setResumeTimestamp] = useState(0);
    const [swapProgress, setSwapProgress] = useState(0);
    const [dronePosOverride, setDronePosOverride] = useState<{ x: number, y: number, z: number } | null>(null);
    const [totalMissionSeconds, setTotalMissionSeconds] = useState(0);

    const SWAP_TIME_MISSION_SEC = 180; // 3 minutes virtual delay

    const routeSimIntervalRef = useRef<number | null>(null);

    // Individual Circle Parameters
    const [b1H, setB1H] = useState(-7.1);
    const [b1R, setB1R] = useState(4.3);
    const [c1H, setC1H] = useState(0.1);
    const [c1R, setC1R] = useState(4.3);
    const [c2H, setC2H] = useState(7.5);
    const [c2R, setC2R] = useState(4.3);
    const [r1H, setR1H] = useState(9.0);
    const [r1R, setR1R] = useState(4.3);

    const [isRadiusLocked, setIsRadiusLocked] = useState(true);
    const [activePreset, setActivePreset] = useState<'DRYERS' | 'VESSELS_IN' | 'VESSELS_OUT' | null>(null);
    const [showDroneModel, setShowDroneModel] = useState(false);

    const [assetCenter, setAssetCenter] = useState({ x: 0, y: 0 });
    const [horizontalOverlap, setHorizontalOverlap] = useState(25);
    const [verticalOverlap, setVerticalOverlap] = useState(25);
    const [standoff, setStandoff] = useState(0.8);
    const [flightSpeed, setFlightSpeed] = useState(0.6);

    const [lidarPoints, setLidarPoints] = useState<TelemetryPoint[]>([]);
    const [flightPath, setFlightPath] = useState<TelemetryPoint[]>([]);
    const [simulatedPath, setSimulatedPath] = useState<TelemetryPoint[]>([]);
    const [missionStartTime, setMissionStartTime] = useState<number | undefined>(undefined);

    const [simVideoFile, setSimVideoFile] = useState<File | null>(null);
    const [simVideoUrl, setSimVideoUrl] = useState<string | null>(null);
    const [simCloudFile, setSimCloudFile] = useState<File | null>(null);
    const [simTrajectoryFile, setSimTrajectoryFile] = useState<File | null>(null);
    const [lasModelName, setLasModelName] = useState<string | null>(null);
    const [importedPoints, setImportedPoints] = useState<TelemetryPoint[] | null>(null);

    const videoInputRef = useRef<HTMLInputElement>(null);
    const cloudInputRef = useRef<HTMLInputElement>(null);
    const trajectoryInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { simulationSpeedRef.current = simulationSpeed; }, [simulationSpeed]);
    useEffect(() => { isAutonomousRunRef.current = checklistStep6; }, [checklistStep6]);

    // Decouple Video from Simulation Duration: allow looping
    useEffect(() => {
        if (videoRef.current && simVideoUrl) {
            const v = videoRef.current;
            const handleLoop = () => { if (isSimulatingRoute) { v.currentTime = 0; v.play().catch(() => { }); } };
            v.addEventListener('ended', handleLoop);
            return () => v.removeEventListener('ended', handleLoop);
        }
    }, [simVideoUrl, isSimulatingRoute]);

    useEffect(() => {
        const startCamera = async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                setVideoStream(stream);
                if (videoRef.current && !simVideoUrl) videoRef.current.srcObject = stream;
            } catch (e) { console.warn("Camera access unavailable."); }
        };
        if (!simVideoFile) startCamera();
        return () => { if (videoStream) videoStream.getTracks().forEach(t => t.stop()); };
    }, [simVideoUrl, simVideoFile]);

    useEffect(() => {
        if (videoRef.current) {
            if (simVideoUrl) { videoRef.current.srcObject = null; videoRef.current.src = simVideoUrl; }
            else if (videoStream) { videoRef.current.srcObject = videoStream; }
        }
    }, [videoStream, simVideoUrl]);

    // Mission Overlay Loop
    useEffect(() => {
        const updateLoop = () => {
            if (canvasRef.current && videoRef.current) {
                const canvas = canvasRef.current, video = videoRef.current, ctx = canvas.getContext('2d');
                if (ctx && video.videoWidth > 0) {
                    const rect = video.getBoundingClientRect();
                    if (canvas.width !== rect.width || canvas.height !== rect.height) { canvas.width = rect.width; canvas.height = rect.height; }
                    ctx.clearRect(0, 0, canvas.width, canvas.height);
                    if (checklistStep6 && aiMode === 'LIVE' && isSimulatingRoute) {
                        aiFindings.forEach((finding) => {
                            if (!finding.box) return;
                            const [ymin, xmin, ymax, xmax] = finding.box, color = severityColors[finding.severity] || '#fff';
                            const x = (xmin / 1000) * canvas.width, y = (ymin / 1000) * canvas.height, w = ((xmax - xmin) / 1000) * canvas.width, h = ((ymax - ymin) / 1000) * canvas.height;
                            ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.strokeRect(x, y, w, h);
                            ctx.fillStyle = color; ctx.font = 'bold 12px monospace'; ctx.fillText(`${finding.type.toUpperCase()}`, x + 5, y - 5);
                        });
                    }
                }
            }
            animationFrameRef.current = requestAnimationFrame(updateLoop);
        };
        updateLoop();
        return () => { if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current); };
    }, [isSimulatingRoute, checklistStep6, aiMode, aiFindings]);

    // Automated Battery Swap Logic (Simulating RTH using egress/ingress logic)
    useEffect(() => {
        let timeout: number;
        if (swapPhase === 'RTH_FLIGHT') {
            // Simulate 4s flight to (0,0,0)
            const startPos = flightPath.find(p => p.timestamp >= resumeTimestamp) || { x: 0, y: 0, z: 0 };
            let elapsed = 0;
            const interval = window.setInterval(() => {
                elapsed += 50;
                const f = Math.min(1, elapsed / 4000);
                setDronePosOverride({
                    x: startPos.x * (1 - f),
                    y: startPos.y * (1 - f),
                    z: startPos.z * (1 - f)
                });
                if (f >= 1) {
                    clearInterval(interval);
                    setSwapPhase('SWAPPING');
                    setTotalMissionSeconds(prev => prev + 240); // 4 min transit
                }
            }, 50);
            return () => clearInterval(interval);
        } else if (swapPhase === 'SWAPPING') {
            setDronePosOverride({ x: 0, y: 0, z: 0 });
            let progress = 0;
            const interval = window.setInterval(() => {
                progress += 1.8; // Takes ~2.7s real time
                setSwapProgress(progress);
                if (progress >= 100) {
                    clearInterval(interval);
                    setBatterySecondsUsed(0);
                    setBatteryCycleCount(c => c + 1);
                    setTotalMissionSeconds(prev => prev + SWAP_TIME_MISSION_SEC);
                    setSwapPhase('RETURN_FLIGHT');
                }
            }, 50);
            return () => clearInterval(interval);
        } else if (swapPhase === 'RETURN_FLIGHT') {
            const endPos = flightPath.find(p => p.timestamp >= resumeTimestamp) || { x: 0, y: 0, z: 0 };
            let elapsed = 0;
            const interval = window.setInterval(() => {
                elapsed += 50;
                const f = Math.min(1, elapsed / 4000);
                setDronePosOverride({
                    x: endPos.x * f,
                    y: endPos.y * f,
                    z: endPos.z * f
                });
                if (f >= 1) {
                    clearInterval(interval);
                    setDronePosOverride(null);
                    setSwapPhase('NONE');
                    setTotalMissionSeconds(prev => prev + 240); // 4 min return
                    handleRouteSimulation(true, resumeTimestamp, true);
                }
            }, 50);
            return () => clearInterval(interval);
        }
    }, [swapPhase, resumeTimestamp, flightPath]);

    const toggleSimulationPause = () => {
        if (!isSimulatingRoute || swapPhase !== 'NONE') return;
        if (isPaused) {
            setIsPaused(false);
            handleRouteSimulation(true, currentTime, true);
        } else {
            setIsPaused(true);
            if (routeSimIntervalRef.current) clearInterval(routeSimIntervalRef.current);
            videoRef.current?.pause();
        }
    };

    const handleRouteSimulation = (isResuming: boolean = false, explicitResumeTime: number = 0, playVideo: boolean = false) => {
        if (flightPath.length === 0) return;
        if (!isResuming && isSimulatingRoute) {
            setIsSimulatingRoute(false);
            setIsPaused(false);
            if (routeSimIntervalRef.current) clearInterval(routeSimIntervalRef.current);
            setCurrentTime(0);
            setBatterySecondsUsed(0);
            setBatteryCycleCount(1);
            setSwapPhase('NONE');
            if (videoRef.current) { videoRef.current.pause(); }
        } else {
            setIsSimulatingRoute(true);
            setIsPaused(false);
            setSimulationComplete(false);
            const shouldPlay = playVideo || isAutonomousRunRef.current;
            if (shouldPlay && videoRef.current) {
                if (!isResuming) videoRef.current.currentTime = 0;
                videoRef.current.play().catch(() => { });
            }
            let t = isResuming ? explicitResumeTime : 0;
            const totalDuration = flightPath[flightPath.length - 1].timestamp;
            routeSimIntervalRef.current = window.setInterval(() => {
                const dt = 0.05 * simulationSpeedRef.current;
                t += dt;
                setCurrentTime(t);
                setBatterySecondsUsed(prev => {
                    const next = prev + dt;
                    if (next >= RTH_TRIGGER_SEC) { startRTHSequence(t); return next; }
                    return next;
                });
                setTotalMissionSeconds(prev => prev + dt);
                if (t >= totalDuration) {
                    setIsSimulatingRoute(false);
                    setSimulationComplete(true);
                    clearInterval(routeSimIntervalRef.current!);
                    if (videoRef.current) videoRef.current.pause();
                }
            }, 50);
        }
    };

    const startRTHSequence = (currentSimTime: number) => {
        if (routeSimIntervalRef.current) clearInterval(routeSimIntervalRef.current);
        if (videoRef.current && !videoRef.current.paused) videoRef.current.pause();

        // Find capture point - 1
        let resumeTime = currentSimTime;
        const captures = flightPath.filter(p => p.forceCapture || !p.isTransit);
        let lastCapIndex = -1;
        for (let i = captures.length - 1; i >= 0; i--) {
            if (captures[i].timestamp < currentSimTime) {
                lastCapIndex = i;
                break;
            }
        }
        if (lastCapIndex > 0) {
            resumeTime = captures[lastCapIndex - 1].timestamp;
        }

        setResumeTimestamp(resumeTime);
        setSwapPhase('RTH_FLIGHT');
    };

    const handleVideoUpload = (e: any) => { const file = e.target.files?.[0]; if (file) { setSimVideoFile(file); setSimVideoUrl(URL.createObjectURL(file)); } };

    const handleTrajectoryUpload = (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
            setSimTrajectoryFile(file); const reader = new FileReader();
            reader.onload = (event) => {
                const pts = parseTrajectoryCsv(event.target?.result as string);
                if (pts.length > 0) {
                    const geom = calculateAssetGeometry(pts);
                    // Filter simulated path to asset boundary
                    const filtered = filterPointsToAssetBoundary(pts, geom, 0.5);
                    setSimulatedPath(filtered);
                    setMissionStartTime(detectMissionStart(filtered));
                    setAssetCenter(geom.center);
                    setB1R(geom.radius);
                    setB1H(geom.minZ);
                    setC1H(geom.cylinderBottomZ);
                    setC1R(geom.radius);
                    setC2R(geom.radius);
                    setC2H(geom.maxZ);
                    setR1H(geom.maxZ + 1.5);
                    setR1R(geom.radius);
                    setPhase('MAPPED');
                    setHomePointSet(true);
                }
            };
            reader.readAsText(file);
        }
    };

    const handleCloudUpload = async (e: any) => {
        const file = e.target.files?.[0];
        if (file) {
            setSimCloudFile(file);
            setLasModelName(file.name);
            try {
                let pts = await (file.name.toLowerCase().endsWith('.las') ? parseLasFile(file) : parseTextPointCloud(file));
                if (pts.length > 0) {
                    const geom = calculateAssetGeometry(pts);
                    // Filter points outside asset boundary
                    const filtered = filterPointsToAssetBoundary(pts, geom, 0.5);
                    setImportedPoints(filtered);
                    setLidarPoints(filtered);
                    setAssetCenter(geom.center);
                    setB1R(geom.radius);
                    setB1H(geom.minZ);
                    setC1H(geom.cylinderBottomZ);
                    setC1R(geom.radius);
                    setC2H(geom.maxZ);
                    setC2R(geom.radius);
                    setR1H(geom.maxZ + 1.5);
                    setR1R(geom.radius);
                    setPhase('MAPPED');
                    setHomePointSet(true);
                }
            } catch (err) { alert("Failed to parse cloud."); }
        }
    };

    const startExploration = () => { setPhase('EXPLORING'); if (!importedPoints) { setLidarPoints([]); } setTimeout(() => { videoRef.current?.play(); }, 100); };
    const stopExploration = () => { videoRef.current?.pause(); setPhase('MAPPED'); if (!importedPoints) setLidarPoints(generateCylinderPointCloud(c2R, 3.0, 20.0, 15000, 0, assetCenter.x, assetCenter.y)); };

    const generateRoute = () => {
        const routeParams = {
            circles: {
                b1: { z: b1H, r: b1R },
                c1: { z: c1H, r: c1R },
                c2: { z: c2H, r: c2R },
                r1: { z: r1H, r: r1R }
            },
            standoff,
            overlapH: horizontalOverlap,
            overlapV: verticalOverlap,
            speed: flightSpeed,
            cameraTilt: 0,
            centerX: assetCenter.x,
            centerY: assetCenter.y,
            originalPath: simulatedPath
        };

        const isVesselCircular = activePreset === 'VESSELS_OUT' || activePreset === 'VESSELS_IN';
        const route = isVesselCircular
            ? generateHorizontalVesselRoute({ ...routeParams, lookInward: activePreset === 'VESSELS_IN' })
            : generateCylindricalRoute(routeParams);

        setFlightPath(route);
        setPhase('PLANNING');
    };

    const clearRoute = () => {
        setFlightPath([]);
        setIsSimulatingRoute(false);
        setIsPaused(false);
        if (routeSimIntervalRef.current) clearInterval(routeSimIntervalRef.current);
        setCurrentTime(0);
        setBatterySecondsUsed(0);
        setBatteryCycleCount(1);
        setSwapPhase('NONE');
        if (videoRef.current) videoRef.current.pause();
        if (phase === 'PLANNING') setPhase('MAPPED');
    };

    const handleSendToInspector = () => {
        if (flightPath.length === 0) return;
        setRouteAccepted(true);
        const geometry = {
            center: assetCenter,
            b1: { z: b1H, r: b1R },
            c1: { z: c1H, r: c1R },
            c2: { z: c2H, r: c2R },
            r1: { z: r1H, r: r1R }
        };
        onTransfer(simVideoFile, flightPath, lidarPoints, verticalOverlap, lasModelName, geometry);
    };

    const handleSendToUav = () => {
        if (flightPath.length === 0) return;
        alert("COMM_LINK_ESTABLISHED: Transmitting mission profile to UAV subsystem.");
    };

    const exportPathCsv = () => {
        if (flightPath.length === 0) return;
        const header = "timestamp x y z qx qy qz qw quality tilt\n";
        const body = flightPath.map(p => `${p.timestamp.toFixed(3)} ${p.x.toFixed(4)} ${p.y.toFixed(4)} ${p.z.toFixed(4)} ${p.qx.toFixed(6)} ${p.qy.toFixed(6)} ${p.qz.toFixed(6)} ${p.qw.toFixed(6)} ${p.quality} ${(p.tilt ?? 0).toFixed(2)}`).join('\n');
        const blob = new Blob([header + body], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `E3_Sim_Path_${Date.now()}.csv`;
        link.click();
    };

    const exportPathJson = () => {
        if (flightPath.length === 0) return;
        const blob = new Blob([JSON.stringify(flightPath, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `E3_Sim_Path_${Date.now()}.json`;
        link.click();
    };

    const activeBaseTime = swapPhase !== 'NONE' ? 0 : (missionStartTime || 0);
    const batteryPct = Math.max(0, Math.round(((BATTERY_MAX_SEC - batterySecondsUsed) / BATTERY_MAX_SEC) * 100));

    const applyDryerPreset = () => {
        setActivePreset('DRYERS');
        setB1H(-7.1); setB1R(0.8);
        setC1H(0.1); setC1R(4.3);
        setC2H(7.5); setC2R(4.3);
        setR1H(9.0); setR1R(4.3);
        setStandoff(0.8);
        setVerticalOverlap(25);
        setHorizontalOverlap(25);
        setPhase('MAPPED');
    };

    const applyVesselInPreset = () => {
        setActivePreset('VESSELS_IN');
        setB1H(-6.5); setB1R(1.0);
        setC1H(-5.0); setC1R(2.5);
        setC2H(10.0); setC2R(2.5);
        setR1H(11.5); setR1R(1.0);
        setStandoff(0.6);
        setVerticalOverlap(50);
        setHorizontalOverlap(50);
        setPhase('MAPPED');
    };

    const applyVesselOutPreset = () => {
        setActivePreset('VESSELS_OUT');
        setB1H(-1.5); setB1R(1.0);
        setC1H(0.0); setC1R(2.5);
        setC2H(15.0); setC2R(2.5);
        setR1H(16.5); setR1R(1.0);
        setStandoff(0.7);
        setVerticalOverlap(50);
        setHorizontalOverlap(50);
        setPhase('MAPPED');
    };

    const handleParamChange = (setter: (val: any) => void, value: any) => {
        setter(value);
        // Keep preset active as the current "mode"
    };

    const handleC1RadiusChange = (val: number) => {
        setC1R(val);
        if (isRadiusLocked) setC2R(val);
    };

    const handleC2RadiusChange = (val: number) => {
        setC2R(val);
        if (isRadiusLocked) setC1R(val);
    };

    const currentGeometry = useMemo(() => ({
        center: assetCenter,
        b1: { z: b1H, r: b1R },
        c1: { z: c1H, r: c1R },
        c2: { z: c2H, r: c2R },
        r1: { z: r1H, r: r1R }
    }), [assetCenter, b1H, b1R, c1H, c1R, c2H, c2R, r1H, r1R]);

    const flightEstimates = useMemo(() => {
        if (!flightPath.length) return { distance: 0, time: 0, photos: 0, area: 0 };
        let distance = 0;
        let photos = 0;
        for (let i = 1; i < flightPath.length; i++) {
            const p = flightPath[i];
            const pPrev = flightPath[i - 1];
            distance += Math.sqrt(Math.pow(p.x - pPrev.x, 2) + Math.pow(p.y - pPrev.y, 2) + Math.pow(p.z - pPrev.z, 2));
            if (p.forceCapture) photos++;
        }
        const time = distance / (flightSpeed || 0.6) / 60;
        const opticalStandoff = Math.max(0.1, standoff);
        const footprint = calculateSurfaceFootprint(opticalStandoff, c1R);
        const areaPerPhoto = (footprint.widthCm / 100) * (footprint.heightCm / 100);
        const area = photos * areaPerPhoto * (1 - verticalOverlap / 100);
        return { distance, time, photos, area };
    }, [flightPath, flightSpeed, standoff, c1R, verticalOverlap]);

    return (
        <div className="flex flex-col h-full bg-slate-400 animate-fadeIn text-left">
            <header className="h-20 bg-slate-300 border-b border-slate-400 flex items-center px-10 justify-between shrink-0 relative z-30 shadow-sm">
                <div className="flex items-center gap-8">
                    <h2 className="text-2xl font-black uppercase tracking-tighter text-[#0000a9] italic">Mission Planner <span className="text-slate-300 font-light not-italic">| E3 Autonomy</span></h2>
                </div>
                <button onClick={onCancel} className="px-6 py-2 border border-slate-400 hover:bg-slate-300 rounded-none text-[10px] font-black uppercase tracking-widest transition text-slate-700">Abort / Back</button>
            </header>

            <main className="flex-1 overflow-hidden p-8 flex gap-8">
                <div className="w-72 bg-slate-400 border border-slate-500 flex flex-col p-4 gap-3 z-20 rounded-none overflow-y-auto shrink-0 custom-scrollbar shadow-xl">
                    <div className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-1 pl-1">Flight Checklist</div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${setupStep === 'READY' ? 'bg-slate-300/50 border-[#0000a9]/30' : 'bg-slate-300 border-slate-400'}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">1. Place UAV</span>{setupStep === 'READY' && <svg className="w-4 h-4 text-[#0000a9]" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
                        <button onClick={() => setSetupStep('READY')} className="mt-2 py-1.5 bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 rounded-none text-[8px] font-black uppercase tracking-widest">Placement Confirmed</button>
                    </div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${homePointSet ? 'bg-slate-300/50 border-[#0000a9]/30' : (setupStep === 'READY' ? 'bg-slate-300 border-slate-400' : 'opacity-30 pointer-events-none')}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">2. Set Home Point</span>{homePointSet && <svg className="w-4 h-4 text-[#0000a9]" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
                        <button onClick={() => setHomePointSet(true)} className="mt-2 py-1.5 bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 rounded-none text-[8px] font-black uppercase tracking-widest">Home Recorded</button>
                    </div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${phase === 'EXPLORING' || phase === 'MAPPED' || phase === 'PLANNING' ? 'bg-slate-300/50 border-[#0000a9]/30' : (homePointSet ? 'bg-slate-300 border-slate-400' : 'opacity-30 pointer-events-none')}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">3. Execute Ingress</span></div>
                        <button onClick={startExploration} className="mt-2 py-1.5 bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 rounded-none text-[8px] font-black uppercase tracking-widest">Start Entry</button>
                    </div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${checklistStep4 ? 'bg-slate-300/50 border-[#0000a9]/30' : (routeAccepted ? 'bg-slate-300 border-slate-400' : 'opacity-30 pointer-events-none')}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">4. Route Locked</span>{checklistStep4 && <svg className="w-4 h-4 text-[#0000a9]" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}</div>
                        <button onClick={() => setChecklistStep4(true)} className="mt-2 py-1.5 bg-[#0000a9]/10 text-[#0000a9] border border-[#0000a9]/20 rounded-none text-[8px] font-black uppercase tracking-widest">Lock Sequence</button>
                    </div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${aiMode ? 'bg-slate-300/50 border-[#0000a9]/30' : (checklistStep4 ? 'bg-slate-300 border-slate-400' : 'opacity-30 pointer-events-none')}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">5. AI Analysis Mode</span></div>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                            <button onClick={() => setAiMode('VIDEO')} className={`py-2 rounded-none text-[8px] font-black uppercase flex flex-col items-center gap-1 transition-all ${aiMode === 'VIDEO' ? 'bg-[#0000a9] text-white' : 'bg-slate-800 border border-white/10 text-slate-200'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg>Video AI</button>
                            <button onClick={() => setAiMode('LIVE')} className={`py-2 rounded-none text-[8px] font-black uppercase flex flex-col items-center gap-1 transition-all ${aiMode === 'LIVE' ? 'bg-[#0000a9] text-white' : 'bg-slate-800 border border-white/10 text-slate-200'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>Live AI</button>
                        </div>
                    </div>
                    <div className={`p-4 rounded-none border transition-all flex flex-col gap-1 relative ${checklistStep6 ? 'bg-slate-300/50 border-[#0000a9]/30' : (aiMode ? 'bg-slate-300/50 border-[#0000a9]/30' : 'opacity-30 pointer-events-none')}`}>
                        <div className="flex justify-between items-start"><span className="text-[11px] font-black uppercase text-slate-900">6. Execute Mission</span>{checklistStep6 && <div className="w-2 h-2 rounded-none bg-red-500 animate-pulse"></div>}</div>
                        <button onClick={() => { setChecklistStep6(true); handleRouteSimulation(true, 0, true); }} className="mt-2 py-3 bg-[#0000a9] hover:bg-[#0000c0] text-white rounded-none text-[9px] font-black uppercase tracking-widest shadow-md">Start Simulation</button>
                    </div>


                    <div className="py-2 flex flex-col gap-2">
                        <div className="text-[10px] font-black text-slate-700 uppercase tracking-widest mb-1 pl-1">Mission Actions</div>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={handleSendToUav} disabled={flightPath.length === 0} className="flex flex-col items-center justify-center gap-1 p-3 rounded-none bg-[#0000a9] text-white text-[10px] font-black uppercase tracking-tight hover:bg-[#0000c0] transition-all disabled:opacity-40 shadow-lg border-2 border-white/10">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                                UAV Link
                            </button>
                            <button onClick={handleSendToInspector} disabled={flightPath.length === 0} className="flex flex-col items-center justify-center gap-1 p-3 rounded-none bg-slate-300 text-[#0000a9] text-[10px] font-black uppercase tracking-tight hover:bg-slate-400 transition-all disabled:opacity-40 shadow-lg border-2 border-[#0000a9]/20">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                Inspect
                            </button>
                            <button onClick={exportPathCsv} disabled={flightPath.length === 0} className="flex flex-col items-center justify-center gap-1 p-3 rounded-none bg-slate-800 text-slate-300 text-[10px] font-black uppercase tracking-tight border-2 border-white/5 hover:bg-slate-300 hover:text-slate-950 transition-all disabled:opacity-40 shadow-lg">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                                CSV Path
                            </button>
                            <button onClick={exportPathJson} disabled={flightPath.length === 0} className="flex flex-col items-center justify-center gap-1 p-3 rounded-none bg-slate-800 text-slate-300 text-[10px] font-black uppercase tracking-tight border-2 border-white/5 hover:bg-slate-300 hover:text-slate-950 transition-all disabled:opacity-40 shadow-lg">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
                                JSON Path
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex-1 flex flex-col gap-4">
                    <div className="flex-1 flex gap-4 min-h-0">
                        <div className="w-[420px] flex flex-col gap-4 shrink-0">
                            <div className="aspect-video bg-black rounded-none border border-slate-800 shadow-xl overflow-hidden relative">
                                <video ref={videoRef} muted playsInline className="w-full h-full object-cover opacity-80" />
                                <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />

                                {swapPhase !== 'NONE' && (
                                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-fadeIn z-50 rounded-none">
                                        {swapPhase === 'RTH_FLIGHT' && (
                                            <>
                                                <div className="w-12 h-12 rounded-none bg-amber-500/20 border border-amber-500 flex items-center justify-center mb-4"><svg className="w-6 h-6 text-amber-500 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z" /></svg></div>
                                                <h3 className="text-amber-500 font-black uppercase tracking-widest text-sm italic">Return to Home (10% Left)</h3>
                                                <p className="text-slate-300 text-[10px] uppercase mt-2">Traversing Exit Path...</p>
                                            </>
                                        )}
                                        {swapPhase === 'SWAPPING' && (
                                            <>
                                                <div className="w-16 h-16 rounded-none bg-[#0000a9]/20 border border-[#0000a9] flex items-center justify-center mb-6"><svg className="w-8 h-8 text-[#0000a9]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg></div>
                                                <h3 className="text-white font-black uppercase tracking-widest text-sm mb-1">Battery Pack Exchange</h3>
                                                <div className="w-full max-w-[200px] h-1.5 bg-slate-800 rounded-none overflow-hidden mb-2 mt-4"><div className="h-full bg-[#0000a9] transition-all duration-100" style={{ width: `${swapProgress}%` }}></div></div>
                                                <span className="text-[9px] font-mono text-[#0000a9]">{Math.round(swapProgress)}%</span>
                                            </>
                                        )}
                                        {swapPhase === 'RETURN_FLIGHT' && (
                                            <>
                                                <div className="w-12 h-12 rounded-none bg-emerald-500/20 border border-emerald-500 flex items-center justify-center mb-4"><svg className="w-6 h-6 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg></div>
                                                <h3 className="text-emerald-500 font-black uppercase tracking-widest text-sm italic">Resuming Mission</h3>
                                                <p className="text-slate-300 text-[10px] uppercase mt-2">Traversing Entry Path...</p>
                                            </>
                                        )}
                                    </div>
                                )}

                                <div className="absolute top-4 left-4 px-3 py-1 bg-black/60 backdrop-blur-md rounded-none text-[8px] font-black uppercase text-white border border-white/10 flex items-center gap-2">
                                    <div className={`w-1.5 h-1.5 rounded-none bg-red-600 ${isSimulatingRoute ? 'animate-pulse' : ''}`}></div>LIVE_SIM_V6.0
                                </div>
                            </div>

                            <div className="flex-1 bg-slate-400 backdrop-blur rounded-none border border-slate-500 p-4 flex flex-col gap-3 overflow-y-auto shadow-lg">
                                <div className="flex flex-col gap-2 mb-2 p-1 border-b border-white/5 bg-black/20">
                                    <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic leading-none pl-1">Asset Configuration</span>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button
                                            onClick={applyDryerPreset}
                                            className={`flex flex-col items-center justify-center gap-0.5 p-1.5 rounded-none transition-all group border ${activePreset === 'DRYERS' ? 'bg-[#0000a9] border-white/30 shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                                        >
                                            <span className={`text-[9px] font-black uppercase ${activePreset === 'DRYERS' ? 'text-white' : 'text-slate-700 group-hover:text-[#0000a9]'}`}>Dryers</span>
                                            <span className={`text-[7px] font-mono leading-none ${activePreset === 'DRYERS' ? 'text-white/80' : 'text-slate-800'}`}>SDP,SDI,CDI</span>
                                        </button>
                                        <button
                                            onClick={applyVesselInPreset}
                                            className={`flex flex-col items-center justify-center gap-0.5 p-1.5 rounded-none transition-all group border ${activePreset === 'VESSELS_IN' ? 'bg-[#0000a9] border-white/30 shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                                        >
                                            <span className={`text-[9px] font-black uppercase ${activePreset === 'VESSELS_IN' ? 'text-white' : 'text-slate-700 group-hover:text-[#0000a9]'}`}>Vessels In</span>
                                            <span className={`text-[7px] font-mono leading-none ${activePreset === 'VESSELS_IN' ? 'text-white/80' : 'text-slate-800'}`}>Agitator Scan</span>
                                        </button>
                                        <button
                                            onClick={applyVesselOutPreset}
                                            className={`flex flex-col items-center justify-center gap-0.5 p-1.5 rounded-none transition-all group border ${activePreset === 'VESSELS_OUT' ? 'bg-[#0000a9] border-white/30 shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                                        >
                                            <span className={`text-[9px] font-black uppercase ${activePreset === 'VESSELS_OUT' ? 'text-white' : 'text-slate-700 group-hover:text-[#0000a9]'}`}>Vessels Out</span>
                                            <span className={`text-[7px] font-mono leading-none ${activePreset === 'VESSELS_OUT' ? 'text-white/80' : 'text-slate-800'}`}>Horizontal</span>
                                        </button>
                                        <button
                                            onClick={() => setShowDroneModel(!showDroneModel)}
                                            className={`flex flex-col items-center justify-center gap-0.5 p-1.5 rounded-none transition-all group border ${showDroneModel ? 'bg-[#0000a9] border-white/30 shadow-[0_0_15px_rgba(0,0,169,0.3)]' : 'bg-slate-300 border-slate-400 hover:border-[#0000a9]/40 hover:bg-slate-400'}`}
                                        >
                                            <span className={`text-[9px] font-black uppercase ${showDroneModel ? 'text-white' : 'text-slate-700 group-hover:text-[#0000a9]'}`}>Drone</span>
                                            <span className={`text-[7px] font-mono leading-none ${showDroneModel ? 'text-white/80' : 'text-slate-800'}`}>Diameter Model</span>
                                        </button>
                                    </div>
                                </div>
                                <div className={`flex flex-col gap-3 transition-all ${!activePreset ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                                    <div className="flex flex-col mb-1 pt-2">
                                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic leading-none">Simulation Assets</span>
                                        {!activePreset && <span className="text-[8px] text-amber-500/80 font-bold uppercase mt-1 animate-pulse">Select Asset Geometry First</span>}
                                    </div>
                                    <button onClick={() => videoInputRef.current?.click()} className={`p-3 rounded-none border transition-all flex items-center gap-3 ${simVideoFile ? 'bg-[#0000a9]/10 border-[#0000a9]/50' : 'bg-slate-800/40 border-white/5'}`}>
                                        <div className={`p-2 rounded-none ${simVideoFile ? 'bg-[#0000a9] text-white' : 'bg-slate-700 text-white'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" /></svg></div>
                                        <div className="flex flex-col text-left overflow-hidden"><span className={`text-[9px] font-black uppercase ${simVideoFile ? 'text-[#0000a9]' : 'text-slate-800'}`}>Inspection Flight Video</span><span className="text-[7px] text-slate-700 font-mono truncate">{simVideoFile ? simVideoFile.name : 'Awaiting MP4'}</span></div>
                                        <input type="file" ref={videoInputRef} className="hidden" accept="video/*" onChange={handleVideoUpload} />
                                    </button>
                                    <button onClick={() => trajectoryInputRef.current?.click()} className={`p-3 rounded-none border transition-all flex items-center gap-3 ${simTrajectoryFile ? 'bg-[#0000a9]/10 border-[#0000a9]/50' : 'bg-slate-800/40 border-white/5'}`}>
                                        <div className={`p-2 rounded-none ${simTrajectoryFile ? 'bg-[#0000a9] text-white' : 'bg-slate-700 text-white'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 7m0 10V7m0 0L9 4" /></svg></div>
                                        <div className="flex flex-col text-left overflow-hidden"><span className={`text-[9px] font-black uppercase ${simTrajectoryFile ? 'text-[#0000a9]' : 'text-slate-800'}`}>Exploration Trajectory</span><span className="text-[7px] text-slate-700 font-mono truncate">{simTrajectoryFile ? simTrajectoryFile.name : 'Awaiting CSV'}</span></div>
                                        <input type="file" ref={trajectoryInputRef} className="hidden" accept=".csv" onChange={handleTrajectoryUpload} />
                                    </button>
                                    <button onClick={() => cloudInputRef.current?.click()} className={`p-3 rounded-none border transition-all flex items-center gap-3 ${simCloudFile ? 'bg-[#0000a9]/10 border-[#0000a9]/50' : 'bg-slate-800/40 border-white/5'}`}>
                                        <div className={`p-2 rounded-none ${simCloudFile ? 'bg-[#0000a9] text-white' : 'bg-slate-700 text-white'}`}><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 00-2-2v-6a2 2 0 01-2-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></div>
                                        <div className="flex flex-col text-left overflow-hidden"><span className={`text-[9px] font-black uppercase ${simCloudFile ? 'text-[#0000a9]' : 'text-slate-800'}`}>Exploration Point Cloud</span><span className="text-[7px] text-slate-700 font-mono truncate">{simCloudFile ? simCloudFile.name : 'Optional .LAS'}</span></div>
                                        <input type="file" ref={cloudInputRef} className="hidden" accept=".las,.laz" onChange={handleCloudUpload} />
                                    </button>
                                </div>

                                <div className="mt-auto pt-4 border-t border-white/5 flex flex-col gap-3">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div className="grid grid-cols-2 gap-2">
                                            <button onClick={generateRoute} className="px-1 py-3 bg-slate-800 hover:bg-slate-300 hover:text-slate-950 text-white rounded-none text-[9px] font-black uppercase tracking-widest border border-white/10 transition-all">Generate</button>
                                            <button onClick={clearRoute} className="px-1 py-3 bg-slate-800 hover:bg-red-500 hover:text-white text-white rounded-none text-[9px] font-black uppercase tracking-widest border border-white/10 transition-all">Clear</button>
                                        </div>
                                        <button onClick={() => handleRouteSimulation(false)} className={`px-3 py-3 border rounded-none text-[9px] font-black uppercase tracking-widest transition-all ${isSimulatingRoute ? 'bg-red-950/40 text-red-500 border-red-500/40 hover:bg-red-500 hover:text-white' : 'bg-[#0000a9]/20 text-[#0000a9] border-[#0000a9]/40 hover:bg-[#0000a9] hover:text-white'}`}>{isSimulatingRoute ? 'Stop Simulation' : 'Play Simulation'}</button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={toggleSimulationPause} disabled={!isSimulatingRoute} className={`px-5 py-3 border rounded-none text-[9px] font-black uppercase tracking-widest transition-all ${isPaused ? 'bg-amber-500 text-slate-950 border-amber-500' : 'bg-slate-800 text-slate-200 border-white/10 hover:text-white'}`}>{isPaused ? 'Resume' : 'Pause'}</button>
                                        <div className="flex-1 bg-slate-950 px-3 py-2 rounded-none border border-white/5 flex items-center gap-3">
                                            <span className="text-[8px] font-black text-slate-300 uppercase whitespace-nowrap">Speed</span>
                                            <input type="range" min="0.1" max="10.0" step="0.1" value={simulationSpeed} onChange={(e) => setSimulationSpeed(parseFloat(e.target.value))} className="w-full h-1.5 bg-white rounded-none appearance-none accent-[#0000a9]" />
                                            <span className="text-[9px] font-mono text-white w-6">{simulationSpeed.toFixed(1)}x</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex-1 bg-slate-900 rounded-none border border-white/5 overflow-hidden relative shadow-2xl">
                            <TrajectoryMapM points={(phase === 'EXPLORING' ? simulatedPath : flightPath)} modelPoints={lidarPoints} previewMode="BOTH" minimal={true} currentTime={currentTime} baseTimestamp={activeBaseTime} mapMode="PLANNING" targetOverlap={verticalOverlap} targetStandoff={standoff} overrideGeometry={(phase === 'MAPPED' || phase === 'PLANNING') ? currentGeometry : undefined} onClose={() => { }} showCapOnly={showCapOnly} hideFrustrums={swapPhase !== 'NONE' || hideCameras} showDroneModel={showDroneModel} posOverride={dronePosOverride} hideLines={hidePath} initialCloudOffset={initialCloudOffset} onAlignChange={onAlignChange} />

                            {/* View control buttons above Flight Estimations */}
                            <div className="absolute top-4 right-4 flex gap-1 bg-slate-950/80 backdrop-blur border border-white/10 p-1 z-50 shadow-xl">
                                <button
                                    onClick={() => {
                                        const mapEl = document.querySelector('[data-map-center-align]') as HTMLButtonElement;
                                        if (mapEl) mapEl.click();
                                    }}
                                    className="px-3 py-1.5 rounded-none text-[9px] font-black uppercase transition-all bg-slate-300/5 text-slate-300 hover:bg-amber-500 hover:text-slate-950"
                                >Center Align</button>
                                <div className="w-px h-4 bg-slate-300/10 mx-1 self-center"></div>
                                <button
                                    onClick={() => {
                                        const btn = document.querySelector('[data-map-view="Side"]') as HTMLButtonElement;
                                        if (btn) btn.click();
                                    }}
                                    className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition"
                                >Side</button>
                                <button
                                    onClick={() => {
                                        const btn = document.querySelector('[data-map-view="Front"]') as HTMLButtonElement;
                                        if (btn) btn.click();
                                    }}
                                    className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition"
                                >Front</button>
                                <button
                                    onClick={() => {
                                        const btn = document.querySelector('[data-map-view="Top"]') as HTMLButtonElement;
                                        if (btn) btn.click();
                                    }}
                                    className="px-3 py-1.5 rounded-none text-[9px] font-bold bg-slate-300/5 text-slate-300 hover:text-white transition"
                                >Top</button>
                            </div>

                            <div className="absolute top-14 right-4 w-72 bg-slate-950/80 backdrop-blur border border-white/10 rounded-none p-4 flex flex-col gap-3 shadow-2xl animate-fadeIn z-50">
                                <div className="flex justify-between items-center border-b border-white/5 pb-2 mb-1">
                                    <div className="flex items-center gap-2">
                                        <svg className="w-3 h-3 text-[#0000a9]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" /></svg>
                                        <span className="text-[9px] font-black text-white uppercase tracking-widest italic">Flight Estimations</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                                    <div className="bg-slate-300/5 p-2 border border-white/5">
                                        <span className="text-[7px] text-slate-800 uppercase block mb-1">Total Dis.</span>
                                        <span className="text-sm font-mono font-black text-[#0000a9]">{flightEstimates.distance.toFixed(1)} <span className="text-[10px] text-slate-800 font-bold not-italic ml-1 italic">m</span></span>
                                    </div>
                                    <div className="bg-slate-300/5 p-2 border border-white/5">
                                        <span className="text-[7px] text-slate-800 uppercase block mb-1">Est. Flight Time</span>
                                        <span className="text-sm font-mono font-black text-[#0000a9]">{flightEstimates.time.toFixed(1)} <span className="text-[10px] text-slate-800 font-bold not-italic ml-1 italic">m</span></span>
                                    </div>
                                    <div className="bg-slate-300/5 p-2 border border-white/5">
                                        <span className="text-[7px] text-slate-800 uppercase block mb-1">Photos</span>
                                        <span className="text-sm font-mono font-black text-[#0000a9]">{flightEstimates.photos} <span className="text-[10px] text-slate-800 font-bold not-italic ml-1 italic">imgs</span></span>
                                    </div>
                                    <div className="bg-slate-300/5 p-2 border border-white/5">
                                        <span className="text-[7px] text-slate-800 uppercase block mb-1">Coverage Area</span>
                                        <span className="text-sm font-mono font-black text-[#0000a9]">{flightEstimates.area.toFixed(1)} <span className="text-[10px] text-slate-800 font-bold not-italic ml-1 italic">m²</span></span>
                                    </div>
                                </div>
                            </div>

                            <div className="absolute bottom-4 right-4 w-56 bg-slate-950/80 backdrop-blur border border-white/10 rounded-none p-4 flex flex-col gap-3 shadow-2xl animate-fadeIn z-50">
                                <div className="flex justify-between items-center border-b border-white/5 pb-2 mb-1"><span className="text-[9px] font-black text-white uppercase tracking-widest italic">Simulation State</span><div className={`w-1.5 h-1.5 rounded-none animate-pulse ${batteryPct > 20 ? 'bg-[#0000a9]' : 'bg-red-500'}`}></div></div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><span className="text-[7px] text-slate-800 uppercase block">Total Ops Time</span><span className="text-xs font-mono font-bold text-white">{(totalMissionSeconds / 60).toFixed(1)}m</span></div>
                                    <div><span className="text-[7px] text-slate-800 uppercase block">Battery</span><span className={`text-xs font-mono font-bold ${batteryPct > 20 ? 'text-white' : 'text-red-400'}`}>{batteryPct}%</span></div>
                                </div>
                                <div className="bg-slate-300/5 p-2 rounded-none border border-white/5">
                                    <div className="flex justify-between items-center mb-1"><span className="text-[8px] font-black text-slate-800 uppercase tracking-tighter italic">Pack Cycle #{batteryCycleCount}</span></div>
                                    <div className="w-full h-1 bg-slate-800 rounded-none overflow-hidden"><div className={`h-full transition-all duration-500 ${batteryPct > 20 ? 'bg-[#0000a9]' : 'bg-red-500'}`} style={{ width: `${batteryPct}%` }}></div></div>
                                </div>
                                <div><span className="text-[7px] text-slate-800 uppercase block">Cycle Recovery Time</span><span className={`text-lg font-black italic ${batteryPct > 20 ? 'text-[#0000a9]' : 'text-red-400'}`}>{Math.max(0, Math.floor(BATTERY_MAX_SEC - batterySecondsUsed))}s</span></div>
                            </div>

                            <div className="absolute bottom-6 left-6 flex gap-2 z-50">
                                <button onClick={() => setShowCapOnly(!showCapOnly)} className={`min-w-[4.5rem] px-4 h-10 backdrop-blur-md rounded-none text-[11px] font-black uppercase tracking-wider transition-all flex items-center justify-center shadow-2xl border ${showCapOnly ? 'bg-[#0000a9]/30 text-[#0000a9] border-[#0000a9]/40 shadow-[#0000a9]/20' : 'bg-black/60 text-slate-300 border-white/10 hover:text-white'}`} title="Path Filter">PATH</button>
                                <button onClick={() => setHideCameras(!hideCameras)} className={`px-4 h-10 backdrop-blur-md rounded-none text-[11px] font-black uppercase tracking-wider transition-all flex items-center justify-center shadow-2xl border ${hideCameras ? 'bg-red-900/30 text-red-500 border-red-500/40' : 'bg-black/60 text-slate-300 border-white/10 hover:text-white'}`}>CAMERAS {hideCameras ? 'OFF' : 'ON'}</button>
                                <button onClick={() => setHidePath(!hidePath)} className={`px-4 h-10 backdrop-blur-md rounded-none text-[11px] font-black uppercase tracking-wider transition-all flex items-center justify-center shadow-2xl border ${hidePath ? 'bg-red-900/30 text-red-500 border-red-500/40' : 'bg-black/60 text-slate-300 border-white/10 hover:text-white'}`}>{hidePath ? 'SHOW' : 'HIDE'} PATH</button>
                            </div>
                        </div>
                    </div>

                    <div className={`h-[320px] bg-slate-400 p-6 rounded-none border border-slate-500 flex flex-col gap-4 shrink-0 ${(phase === 'MAPPED' || phase === 'PLANNING') && !checklistStep6 ? 'opacity-100' : 'opacity-40 pointer-events-none'} transition-all shadow-xl`}>
                        <div className="flex justify-between items-center border-b border-white/5 pb-2">
                            <h4 className="text-[10px] font-black text-slate-700 uppercase tracking-widest italic leading-none">Autonomous Route Parameters</h4>
                        </div>

                        <div className="flex-1 grid grid-cols-2 gap-x-12 gap-y-3 overflow-y-auto custom-scrollbar pr-2 relative">
                            {/* B1 Circle */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">B1 Height (Z)</span><span className="text-white font-mono">{b1H.toFixed(2)}</span></div>
                                <input type="range" min="-15.0" max="15.0" step="0.1" value={b1H} onChange={(e) => handleParamChange(setB1H, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">B1 Radius (R)</span><span className="text-white font-mono">{b1R.toFixed(2)}</span></div>
                                <input type="range" min="0.1" max="15.0" step="0.1" value={b1R} onChange={(e) => handleParamChange(setB1R, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>

                            {/* C1 Circle */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">C1 Height (Z)</span><span className="text-white font-mono">{c1H.toFixed(2)}</span></div>
                                <input type="range" min="-15.0" max="15.0" step="0.1" value={c1H} onChange={(e) => handleParamChange(setC1H, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>
                            <div className="space-y-1 relative">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">C1 Radius (R)</span><span className="text-white font-mono">{c1R.toFixed(2)}</span></div>
                                <input type="range" min="0.1" max="15.0" step="0.1" value={c1R} onChange={(e) => handleC1RadiusChange(parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />

                                <div className="absolute -bottom-7 right-1/2 translate-x-1/2 z-30">
                                    <button
                                        onClick={() => setIsRadiusLocked(!isRadiusLocked)}
                                        className={`w-6 h-6 rounded-none flex items-center justify-center transition-all border shadow-lg ${isRadiusLocked ? 'bg-[#0000a9] border-white/30 text-white' : 'bg-slate-800 border-white/10 text-slate-200 hover:text-white'}`}
                                        title={isRadiusLocked ? 'Unlock radii' : 'Link C1/C2 radii'}
                                    >
                                        {isRadiusLocked ? (
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                        ) : (
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h2V7a6 6 0 10-12 0v4h2V7a4 4 0 014-4z" /></svg>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* C2 Circle */}
                            <div className="space-y-1 mt-4">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">C2 Height (Z)</span><span className="text-white font-mono">{c2H.toFixed(2)}</span></div>
                                <input type="range" min="-15.0" max="15.0" step="0.1" value={c2H} onChange={(e) => handleParamChange(setC2H, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>
                            <div className="space-y-1 mt-4">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">C2 Radius (R)</span><span className="text-white font-mono">{c2R.toFixed(2)}</span></div>
                                <input type="range" min="0.1" max="15.0" step="0.1" value={c2R} onChange={(e) => handleC2RadiusChange(parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>

                            {/* R1 Circle */}
                            <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">R1 Height (Z)</span><span className="text-white font-mono">{r1H.toFixed(2)}</span></div>
                                <input type="range" min="-15.0" max="15.0" step="0.1" value={r1H} onChange={(e) => handleParamChange(setR1H, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>
                            <div className="space-y-1">
                                <div className="flex justify-between text-[9px] font-bold uppercase"><span className="text-white">R1 Radius (R)</span><span className="text-white font-mono">{r1R.toFixed(2)}</span></div>
                                <input type="range" min="0.1" max="15.0" step="0.1" value={r1R} onChange={(e) => handleParamChange(setR1R, parseFloat(e.target.value))} className="w-full h-1 bg-white rounded-none appearance-none accent-[#0000a9] cursor-pointer" />
                            </div>
                        </div>

                        <div className="mt-auto grid grid-cols-4 gap-4 pt-3 border-t border-white/5">
                            <div className="space-y-1"><span className="text-[8px] font-bold text-slate-700 uppercase block">Standoff</span><div className="flex items-center gap-2"><input type="range" min="0.20" max="0.8" step="0.05" value={standoff} onChange={(e) => handleParamChange(setStandoff, parseFloat(e.target.value))} className="flex-1 h-1 bg-white rounded-none appearance-none accent-[#0000a9]" /><span className="text-[9px] font-mono text-[#0000a9]">{standoff}m</span></div></div>
                            <div className="space-y-1"><span className="text-[8px] font-bold text-slate-700 uppercase block">Vertical Sync (Frustum Overlap)</span><div className="flex items-center gap-2"><input type="range" min="0" max="80" step="5" value={verticalOverlap} onChange={(e) => handleParamChange(setVerticalOverlap, parseInt(e.target.value))} className="flex-1 h-1 bg-white rounded-none appearance-none accent-[#0000a9]" /><span className="text-[9px] font-mono text-[#0000a9]">{verticalOverlap}%</span></div></div>
                            <div className="space-y-1"><span className="text-[8px] font-bold text-slate-700 uppercase block">Horizontal Sync (Side Overlap)</span><div className="flex items-center gap-2"><input type="range" min="0" max="80" step="5" value={horizontalOverlap} onChange={(e) => handleParamChange(setHorizontalOverlap, parseInt(e.target.value))} className="flex-1 h-1 bg-white rounded-none appearance-none accent-[#0000a9]" /><span className="text-[9px] font-mono text-[#0000a9]">{horizontalOverlap}%</span></div></div>
                            <div className="space-y-1"><span className="text-[8px] font-bold text-slate-700 uppercase block">Velocity</span><div className="flex items-center gap-2"><input type="range" min="0.1" max="2.0" step="0.1" value={flightSpeed} onChange={(e) => handleParamChange(setFlightSpeed, parseFloat(e.target.value))} className="flex-1 h-1 bg-white rounded-none appearance-none accent-[#0000a9]" /><span className="text-[9px] font-mono text-[#0000a9]">{flightSpeed}m/s</span></div></div>
                        </div>
                    </div>
                </div>
            </main>
        </div>

    );
};
